import React from 'react';

// URL dei loghi reali
const nuvolettaUrl = 'https://customer-assets.emergentagent.com/job_agenzia-services/artifacts/ovqir4my_logo%20nuvoletta%20energy%20multiservizi.png';
const fullLogoUrl = 'https://customer-assets.emergentagent.com/job_agenzia-services/artifacts/9p0zj7hd_logo%20energy%20multiservizi.png';

// Aspect-ratio nativi degli asset (computati dal logo originale).
// Usiamo width/height espliciti per azzerare il CLS durante il caricamento.
const NUVOLETTA_AR = 1;     // ~quadrata
const FULL_LOGO_AR = 3.5;   // logo orizzontale 3.5:1

// Real Energy Multiservizi Logo using actual images
export const EnergyLogo = ({ 
  variant = 'full', // 'full' (icon+text), 'icon' (solo nuvoletta), 'text' (solo testo), 'header' (per header scuro - solo testo bianco)
  size = 'md', // 'sm', 'md', 'lg', 'xl'
  className = ''
}) => {
  const sizes = {
    sm: { icon: 24, text: 100, gap: 'gap-1', padding: '2px 4px', fontSize: 12, subSize: 6 },
    md: { icon: 36, text: 140, gap: 'gap-2', padding: '4px 8px', fontSize: 18, subSize: 9 },
    lg: { icon: 48, text: 180, gap: 'gap-3', padding: '6px 10px', fontSize: 24, subSize: 12 },
    xl: { icon: 64, text: 220, gap: 'gap-4', padding: '8px 12px', fontSize: 32, subSize: 16 },
  };

  const { icon, text, gap, padding, fontSize, subSize } = sizes[size];
  // Dimensioni esplicite per CLS
  const iconHeight = Math.round(icon * 0.7);
  const iconWidth = Math.round(iconHeight * NUVOLETTA_AR);
  const iconHeightFull = Math.round(icon * 0.85);
  const iconWidthFull = Math.round(iconHeightFull * NUVOLETTA_AR);
  const textHeight = Math.round(text * 0.5);
  const textWidth = Math.round(textHeight * FULL_LOGO_AR);

  // Variant 'header' - Logo BIANCO per header scuro (icona in box + testo bianco)
  if (variant === 'header') {
    return (
      <div className={`flex items-center ${gap} ${className}`}>
        <div 
          className="rounded-lg overflow-hidden flex items-center justify-center"
          style={{ backgroundColor: 'rgba(255,255,255,0.95)', padding: '4px 6px' }}
        >
          <img 
            src={nuvolettaUrl}
            alt="Energy"
            width={iconWidth}
            height={iconHeight}
            loading="lazy"
            decoding="async"
            style={{ height: iconHeight, width: 'auto' }}
            className="object-contain"
          />
        </div>
        <div className="flex flex-col leading-none">
          <span 
            className="font-heading font-bold tracking-tight text-white"
            style={{ fontSize }}
          >
            ENERGY
          </span>
          <span 
            className="font-medium tracking-wider text-cyan-400"
            style={{ fontSize: subSize }}
          >
            multiservizi
          </span>
        </div>
      </div>
    );
  }

  if (variant === 'icon') {
    return (
      <div 
        className={`rounded-lg overflow-hidden ${className}`}
        style={{ backgroundColor: 'rgba(255,255,255,0.95)', padding }}
      >
        <img 
          src={nuvolettaUrl}
          alt="Energy Multiservizi"
          width={iconWidthFull}
          height={iconHeightFull}
          loading="lazy"
          decoding="async"
          style={{ height: iconHeightFull, width: 'auto' }}
          className="object-contain"
        />
      </div>
    );
  }

  if (variant === 'text') {
    return (
      <div 
        className={`rounded-lg overflow-hidden ${className}`}
        style={{ backgroundColor: 'rgba(255,255,255,0.95)', padding }}
      >
        <img 
          src={fullLogoUrl}
          alt="Energy Multiservizi"
          width={textWidth}
          height={textHeight}
          loading="lazy"
          decoding="async"
          style={{ height: textHeight, width: 'auto' }}
          className="object-contain"
        />
      </div>
    );
  }

  // Full variant: icon in white box + text beside
  return (
    <div className={`flex items-center ${gap} ${className}`}>
      <div 
        className="rounded-lg overflow-hidden"
        style={{ backgroundColor: 'rgba(255,255,255,0.95)', padding }}
      >
        <img 
          src={nuvolettaUrl}
          alt="Energy"
          width={iconWidthFull}
          height={iconHeightFull}
          loading="lazy"
          decoding="async"
          style={{ height: iconHeightFull, width: 'auto' }}
          className="object-contain"
        />
      </div>
      <div className="flex flex-col leading-none">
        <span 
          className="font-heading font-bold tracking-tight bg-gradient-to-r from-cyan-400 to-blue-600 bg-clip-text text-transparent"
          style={{ fontSize: icon * 0.5 }}
        >
          ENERGY
        </span>
        <span 
          className="font-medium tracking-wider text-slate-600"
          style={{ fontSize: icon * 0.25 }}
        >
          multiservizi
        </span>
      </div>
    </div>
  );
};

// Compact white-on-dark version for footers / banners
export const EnergyLogoCompact = ({ size = 24, className = '' }) => {
  const w = Math.round(size * NUVOLETTA_AR);
  return (
    <img
      src={nuvolettaUrl}
      alt="Energy"
      width={w}
      height={size}
      loading="lazy"
      decoding="async"
      className={`object-contain ${className}`}
      style={{ height: size, width: w }}
    />
  );
};

// Standalone icon (white box + nuvoletta), per favicon-like positioning
export const EnergyIcon = ({ size = 32, className = '' }) => {
  const inner = Math.round(size * 0.8);
  return (
    <div
      className={`rounded-lg overflow-hidden flex items-center justify-center ${className}`}
      style={{ backgroundColor: 'rgba(255,255,255,0.95)', padding: size > 40 ? '8px' : '4px' }}
    >
      <img
        src={nuvolettaUrl}
        alt="Energy Multiservizi"
        width={Math.round(inner * NUVOLETTA_AR)}
        height={inner}
        loading="lazy"
        decoding="async"
        className="object-contain"
        style={{ height: inner, width: 'auto' }}
      />
    </div>
  );
};

// Full logo stacked (per Login / Hero) - logo orizzontale Energy Multiservizi
export const EnergyLogoStacked = ({ size = 'md', className = '' }) => {
  const heights = { sm: 60, md: 100, lg: 150, xl: 200 };
  const h = heights[size] ?? heights.md;
  const w = Math.round(h * FULL_LOGO_AR);
  return (
    <div
      className={`rounded-2xl overflow-hidden ${className}`}
      style={{
        backgroundColor: 'rgba(255,255,255,0.95)',
        padding: '16px 24px',
        boxShadow: '0 4px 30px rgba(6, 182, 212, 0.15)',
      }}
    >
      <img
        src={fullLogoUrl}
        alt="Energy Multiservizi"
        width={w}
        height={h}
        loading="eager"
        decoding="async"
        className="object-contain"
        style={{ height: h, width: 'auto' }}
      />
    </div>
  );
};

export default EnergyLogo;
